import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

export default function RoleCRUD() {
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const navigate = useNavigate();

  // ✅ Fetch all roles
  const fetchRoles = async () => {
    try {
      const token = sessionStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/roles/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch roles");

      const data = await res.json();
      setRoles(data);
    } catch (error) {
      toast.error("Failed to load roles");
    }
  };


  useEffect(() => {
    fetchRoles();
  }, []);

  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
      return;
    }

    // ✅ check admin role
    if (!roles.includes("ROLE_ADMIN")) {
      toast.error("You are not authorized to access this page.");
      navigate("/");
      return;
    }
  }, [navigate]);


  // ➕ Add new role
  const handleAdd = async () => {
    if (!newRole.trim()) {
      toast.warning("Please enter a role name.");
      return;
    }

    const token = sessionStorage.getItem("token");

    const res = await fetch(
      `${API_BASE_URL}/api/roles/add`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newRole }),
      }
    );

    if (res.ok) {
      toast.success("Role added successfully!");
      setNewRole("");
      fetchRoles();
    } else {
      const msg = await res.text();
      toast.error(msg);
    }
  };


  // 💾 Update role
  const handleUpdate = async (id) => {
    if (!editName.trim()) {
      toast.warning("Please enter a valid name.");
      return;
    }

    const token = sessionStorage.getItem("token");

    const res = await fetch(
      `${API_BASE_URL}/api/roles/update/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: editName }),
      }
    );

    if (res.ok) {
      toast.success("Role updated successfully!");
      setEditingId(null);
      fetchRoles();
    } else {
      const msg = await res.text();
      toast.error(msg);
    }
  };


  // 🗑️ Delete role
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this role?")) return;

    const token = sessionStorage.getItem("token");

    const res = await fetch(
      `${API_BASE_URL}/api/roles/delete/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (res.ok) {
      toast.info("Role deleted.");
      fetchRoles();
    } else {
      const msg = await res.text();
      toast.error(msg);
    }
  };


  return (
    <>
      {/* ✅ Fixed Navbar */}
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      {/* ✅ Full-page layout */}
      <div
        className="d-flex flex-column p-4"
        style={{
          minHeight: "100vh",
          backgroundColor: "#F5F7FB",
          marginTop: "110px",
        }}
      >
        <h3 className="fw-bold text-dark mb-4 text-start">
          Role Management
        </h3>

        <div className="row flex-grow-1 g-4">
          {/* ✅ Left Column - Add Role */}
          <div className="col-md-4 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{
                borderRadius: "12px",
                height: "fit-content",
                alignSelf: "flex-start",
              }}
            >
              <div
                className="card-header fw-semibold"
                style={{
                  backgroundColor: "#DFE4EB",
                  color: "black",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                Add New Role
              </div>
              <div className="card-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Enter role name"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                />
                <button
                  className="btn text-dark w-40"
                  style={{ backgroundColor: "#DFE4EB" }}
                  onClick={handleAdd}
                >
                  <FaPlus className="me-2" />
                  Add Role
                </button>
              </div>
            </div>
          </div>

          {/* ✅ Right Column - Role List */}
          <div className="col-md-8 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-header fw-semibold d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#DFE4EB",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <span>All Roles</span>
                <span className="badge bg-secondary">{roles.length} Total</span>
              </div>

              <div
                className="card-body p-0"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <ul className="list-group list-group-flush">
                  {roles.length === 0 && (
                    <li className="list-group-item text-muted text-center py-3">
                      No roles found.
                    </li>
                  )}
                  {roles.map((role) => (
                    <li
                      key={role.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {editingId === role.id ? (
                        <input
                          type="text"
                          className="form-control me-2"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{ maxWidth: "300px" }}
                        />
                      ) : (
                        <span className="fw-medium">{role.name}</span>
                      )}

                      <div>
                        {editingId === role.id ? (
                          <>
                            <button
                              className="btn btn-success btn-sm me-2"
                              onClick={() => handleUpdate(role.id)}
                            >
                              <FaSave /> Save
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}
                            >
                              <FaTimes /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn  btn-sm me-2"
                              onClick={() => {
                                setEditingId(role.id);
                                setEditName(role.name);
                              }}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn  btn-sm"
                              onClick={() => handleDelete(role.id)}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
